import React, { useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { Component, TableConfig, TableColumn, Viewport } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useComponentDataResolver } from "@/services/component-data/useComponentDataResolver";
import { useDataWriteback } from "@/hooks/useDataWriteback";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Plus,
} from "lucide-react";
import { TableLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";
import { TableActionsCell } from "./TableActionsCell";
import {
  getBreakpointFromTableViewport,
  getVisibleTableColumns,
} from "./table-responsive-columns";
import {
  TableRowDataEditor,
} from "./TableRowDataEditor";
import { createTableRowEditorFields } from "./table-row-editor-fields";
import {
  canMutateLocalTableRows,
  commitLocalTableRows,
  generateLocalTableRowId,
  getTableRowRecordId,
  getTableRowWritebackConfig,
} from "./table-local-row-mutations";

interface BasicTableProps {
  component: Component;
  viewport?: Viewport;
}

type TableRowData = Record<string, unknown> & {
  id?: string;
};

type RowEditorState =
  | {
      mode: "create";
      rowData: TableRowData;
    }
  | {
      mode: "edit";
      rowIndex: number;
      rowData: TableRowData;
    };

type TableStyle = React.CSSProperties & {
  "--tm-row-hover"?: string;
};

const FORBIDDEN_STYLE_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const defaultColumns: TableColumn[] = [
  {
    id: "name",
    accessorKey: "name",
    header: "Name",
    enableSorting: true,
    enableColumnFilter: true,
  },
  {
    id: "email",
    accessorKey: "email",
    header: "Email",
    enableSorting: true,
    enableColumnFilter: true,
  },
  {
    id: "role",
    accessorKey: "role",
    header: "Role",
    enableSorting: true,
    enableColumnFilter: true,
  },
];

const defaultData: Record<string, unknown>[] = [];

const normalizeAccessorToken = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findAccessorKey = (row: Record<string, unknown>, accessorKey: string): string | undefined => {
  if (accessorKey in row) return accessorKey;

  const rowKeys = Object.keys(row);
  const lowerAccessorKey = accessorKey.toLowerCase();
  const exactKey = rowKeys.find(key => key.toLowerCase() === lowerAccessorKey);
  if (exactKey) return exactKey;

  const aggregatePrefixes = ['last_', 'avg_', 'sum_', 'count_', 'min_', 'max_', 'none_', 'first_'];
  for (const prefix of aggregatePrefixes) {
    if (lowerAccessorKey.startsWith(prefix)) {
      const strippedKey = accessorKey.slice(prefix.length);
      const matchedKey = findAccessorKey(row, strippedKey);
      if (matchedKey) return matchedKey;
    }
  }

  const normalizedAccessorKey = normalizeAccessorToken(accessorKey);
  return rowKeys.find(key => {
    const normalizedKey = normalizeAccessorToken(key);
    return normalizedKey === normalizedAccessorKey
      || normalizedAccessorKey.startsWith(normalizedKey)
      || normalizedKey.startsWith(normalizedAccessorKey);
  });
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getStringProperty = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const getStringOrNumberProperty = (
  value: unknown,
): string | number | undefined =>
  typeof value === "string" || typeof value === "number" ? value : undefined;

const extractRenderableTableStyle = (
  bag: Record<string, unknown> | undefined,
): React.CSSProperties => {
  if (!bag) return {};

  const result: React.CSSProperties = {};
  Object.entries(bag).forEach(([key, value]) => {
    if (FORBIDDEN_STYLE_KEYS.has(key)) return;
    if (typeof value === "string" || typeof value === "number") {
      Object.assign(result, { [key]: value });
    }
  });
  return result;
};

const isPaginationButtonSize = (
  value: unknown,
): value is "default" | "sm" | "lg" | "icon" =>
  value === "default" ||
  value === "sm" ||
  value === "lg" ||
  value === "icon";

function isInteractiveTableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  const interactiveElement = target.closest("button,input,select,textarea,a,[role='button']");
  if (!(interactiveElement instanceof HTMLElement)) return false;

  const rowElement = target.closest("[data-table-row-index]");
  if (rowElement instanceof HTMLElement) {
    return rowElement.contains(interactiveElement);
  }

  return true;
}

function getDelegatedTableRowIndex(target: EventTarget | null): number | null {
  if (!(target instanceof HTMLElement)) return null;

  const rowElement = target.closest("[data-table-row-index]");
  if (!(rowElement instanceof HTMLElement)) return null;

  const rowIndex = Number(rowElement.dataset.tableRowIndex);
  return Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null;
}

const isTableColumn = (value: unknown): value is TableColumn => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.accessorKey === "string" &&
    typeof value.header === "string"
  );
};

export const BasicTable: React.FC<BasicTableProps> = ({
  component,
  viewport,
}) => {
  const { properties, style } = component;
  const mobileViewport = viewport === Viewport.MOBILE;
  const hasLegacyDefaultMaxHeight =
    properties?.maxHeight === 400 &&
    properties?.enablePagination !== false &&
    properties?.dashboardMode !== true;
  const hasLegacyDefaultFixedHeight =
    properties?.enablePagination !== false &&
    properties?.dashboardMode !== true &&
    component.size?.height === 400 &&
    (style?.height === undefined || style.height === 400 || style.height === "400px") &&
    (properties?.height === undefined || properties.height === 400 || properties.height === "400px");
  const styleRecord = isRecord(style) ? style : undefined;

  // Strip width (handled by ComponentWrapper) AND typography (table has its own Tailwind type system)
  const {
    width: _w, minWidth: _mw, maxWidth: _xw,
    fontSize: _fs, fontWeight: _fw, fontFamily: _ff, fontStyle: _fst,
    color: _clr, letterSpacing: _ls, lineHeight: _lh,
    textTransform: _tt, textDecoration: _td, textAlign: _ta,
    ...rawVisualStyle
  } = styleRecord ?? {};
  const visualStyle = extractRenderableTableStyle(rawVisualStyle);

  // Track window width for responsive column hiding
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resolvedData = useComponentDataResolver(component);
  const explicitMaxHeight =
    typeof properties?.maxHeight === 'number' && !hasLegacyDefaultMaxHeight
      ? properties.maxHeight
      : undefined;
  const usesContentDrivenHeight = explicitMaxHeight === undefined && properties?.enablePagination !== false;
  if (usesContentDrivenHeight && hasLegacyDefaultFixedHeight) {
    delete visualStyle.height;
    delete visualStyle.minHeight;
    delete visualStyle.maxHeight;
  }

  // Card styling: add card chrome if component.style doesn't already set a background
  const hasCustomBackground = !!(visualStyle.background || visualStyle.backgroundColor);
  const cardClasses = hasCustomBackground
    ? `w-full ${usesContentDrivenHeight ? '' : 'h-full'} flex flex-col ${usesContentDrivenHeight ? 'overflow-visible' : 'overflow-hidden'}`
    : `w-full ${usesContentDrivenHeight ? '' : 'h-full'} flex flex-col ${usesContentDrivenHeight ? 'overflow-visible' : 'overflow-hidden'} bg-card/50 border border-border/50 rounded-lg shadow-sm`;
  const cardStyle: React.CSSProperties = { ...visualStyle, padding: visualStyle.padding ?? '16px' };

  const loading = resolvedData.loading;
  const error = resolvedData.error?.message ?? null;
  const reconnectMessage = resolvedData.reconnect?.message ?? null;
  const errorMessage = reconnectMessage ?? error;

  // Auto-generate columns from data when properties.columns is not set
  const autoColumns: TableColumn[] = React.useMemo(() => {
    if (resolvedData.rows.length === 0) return [];

    // Fields that are IDataRecord metadata (should never be user-facing columns)
    const metadataFields = new Set([
      '_id', 'recordId', 'record_id', 'documentId', 'document_id',
      '__v', 'schemaId', 'schemaVersion', 'groupId',
      'organizationId', 'metadata', 'createdAt', 'updatedAt',
    ]);

    return Object.keys(resolvedData.rows[0])
      .filter(key => !metadataFields.has(key))
      .filter(key => {
        // Skip complex nested objects (e.g. un-flattened .data or .metadata)
        const val = resolvedData.rows[0][key];
        return val === null || val === undefined || typeof val !== 'object'
          || val instanceof Date || Array.isArray(val);
      })
      .map(key => ({
        id: key,
        accessorKey: key,
        header: key
          // Strip unambiguous aggregation prefixes for cleaner display.
          // Keep first_/last_ because they are common data field names (first_name/last_name).
          .replace(/^(avg|sum|count|min|max|none)_/, '')
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .replace(/\b\w/g, s => s.toUpperCase())
          .trim(),
        enableSorting: true,
        enableColumnFilter: true,
      }));
  }, [resolvedData.rows]);

  const configuredColumns = Array.isArray(properties?.columns) && properties.columns.every(isTableColumn)
    ? properties.columns
    : null;
  const pageSize = typeof properties?.pageSize === "number" ? properties.pageSize : 10;
  const maxHeight =
    explicitMaxHeight ?? (properties?.enablePagination === false ? 400 : undefined);
  const baseColumns = configuredColumns !== null && configuredColumns.length > 0
    ? configuredColumns
    : autoColumns.length > 0
      ? autoColumns
      : defaultColumns;
  const visibleColumns = getVisibleTableColumns(baseColumns, properties);
  const canEditLocalRows = canMutateLocalTableRows(component);
  const tableWritebackConfig = React.useMemo(
    () => getTableRowWritebackConfig(component),
    [component],
  );
  const {
    createRecord,
    updateRecord,
    deleteRecord,
    resolveWriteTarget,
  } = useDataWriteback({
    writebackConfig: tableWritebackConfig,
    componentId: component.id,
  });
  const canCreateSourceRows = resolveWriteTarget("create").status === "ready";
  const canUpdateSourceRows = resolveWriteTarget("update").status === "ready";
  const canDeleteSourceRows = resolveWriteTarget("delete").status === "ready";
  const canMutateSourceRows =
    tableWritebackConfig !== undefined &&
    (canCreateSourceRows || canUpdateSourceRows || canDeleteSourceRows);
  const canOpenRowEditor = baseColumns.length > 0;
  const canEditRows = canEditLocalRows || canUpdateSourceRows;
  const canInteractWithRows = canOpenRowEditor;
  const [localRowsOverride, setLocalRowsOverride] = React.useState<TableRowData[] | null>(null);
  const [rowEditorState, setRowEditorState] = React.useState<RowEditorState | null>(null);

  React.useEffect(() => {
    setLocalRowsOverride(null);
    setRowEditorState(null);
  }, [component.id, properties?.data]);

  const resolvedTableData = React.useMemo(
    () =>
      (canEditLocalRows || canMutateSourceRows) && localRowsOverride !== null
        ? localRowsOverride
        : resolvedData.rows.length > 0
          ? resolvedData.rows
          : defaultData,
    [canEditLocalRows, canMutateSourceRows, localRowsOverride, resolvedData.rows],
  );
  const canAddLocalRows =
    (canEditLocalRows || canCreateSourceRows) &&
    properties?.dataAdditionsEnabled === true &&
    baseColumns.length > 0;

  const tableConfig: TableConfig = {
    columns: visibleColumns,
    data: resolvedTableData,
    enableSorting: properties?.enableSorting !== false && properties?.dashboardMode !== true,
    enableFiltering: properties?.enableFiltering !== false && properties?.dashboardMode !== true,
    enableGlobalFilter: properties?.enableGlobalFilter !== false && properties?.dashboardMode !== true,
    enablePagination: properties?.enablePagination !== false && properties?.dashboardMode !== true,
    pageSize,
    striped: properties?.striped !== false,
    hoverable: properties?.hoverable !== false && properties?.dashboardMode !== true,
    maxHeight,
  };

  // Diagnostic: warn when column keys don't match data keys
  React.useEffect(() => {
    if (tableConfig.data.length > 0 && tableConfig.columns.length > 0) {
      const firstRow = tableConfig.data[0];
      const dataKeys = Object.keys(firstRow);
      const columnKeys = tableConfig.columns.map((c: TableColumn) => c.accessorKey);
      const mismatches = columnKeys.filter(k => !findAccessorKey(firstRow, k));
      if (mismatches.length > 0) {
        console.warn(`[BasicTable:${component.id}] Column/data key mismatch`, {
          unmatchedColumns: mismatches,
          columnAccessorKeys: columnKeys,
          dataKeys: dataKeys.slice(0, 15),
          dataSource: resolvedData.source.kind,
        });
      } else {
        console.debug(`[BasicTable:${component.id}] Data resolved`, {
          rows: tableConfig.data.length,
          columns: columnKeys.length,
          dataSource: resolvedData.source.kind,
        });
      }
    }
  }, [
    component.id,
    resolvedData.source.kind,
    tableConfig.columns,
    tableConfig.data,
  ]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState<string>("");

  // Resolve a cell value from a row using fallback strategies
  const resolveAccessor = React.useCallback((row: Record<string, unknown>, accessorKey: string) => {
    // 1. Direct match (works for flat data and auto-columns)
    const directKey = findAccessorKey(row, accessorKey);
    if (directKey) return row[directKey];
    // 3. Aggregation-prefixed variants (handles queryTemplateData results)
    const prefixes = ['last_', 'avg_', 'sum_', 'count_', 'min_', 'max_', 'none_', 'first_'];
    for (const prefix of prefixes) {
      const key = `${prefix}${accessorKey}`;
      const prefixKey = findAccessorKey(row, key);
      if (prefixKey) return row[prefixKey];
    }
    // 4. Nested .data fallback (safety net for un-flattened IDataRecord)
    if (isRecord(row.data)) {
      const nestedKey = findAccessorKey(row.data, accessorKey);
      if (nestedKey) return row.data[nestedKey];
    }
    return undefined;
  }, []);

  const columns = React.useMemo(
    () =>
      tableConfig.columns
        // Filter columns based on hideBelow metadata
        .filter((col) => {
          if (getBreakpointFromTableViewport(viewport)) return true;
          const hideBelow = col.meta?.hideBelow;
          if (typeof hideBelow === 'number') {
            return windowWidth > hideBelow;
          }
          return true;
        })
        .map((col) => ({
        // Use accessorFn for flexible key resolution (handles aggregation prefixes, nested data)
        id: col.id || col.accessorKey,
        accessorFn: (row: Record<string, unknown>) => resolveAccessor(row, col.accessorKey),
        header: col.header,
        enableSorting: col.enableSorting !== false,
        enableColumnFilter: col.enableColumnFilter !== false,
        size: col.size,
        minSize: col.minSize || 50,
        maxSize: col.maxSize || 500,
        cell: ({ getValue }: { getValue: () => unknown }) => {
          const value = getValue();
          if (value === null || value === undefined) return "";
          if (value instanceof Date) return value.toLocaleDateString();
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        },
        // Only add cell property for actions column - otherwise TanStack Table uses default renderer
        ...(col.accessorKey.toLowerCase() === 'actions' && {
          cell: ({ row }: { row: { id: string; original: Record<string, unknown> } }) => {
            const actions = resolveAccessor(row.original, col.accessorKey);
            return (
              <TableActionsCell
                tableId={component.id}
                rowId={getTableRowRecordId(row.original) ?? row.id}
                rowData={row.original}
                actions={typeof actions === "string" ? actions : undefined}
              />
            );
          },
        }),
      })),
    [tableConfig.columns, component.id, viewport, windowWidth, resolveAccessor],
  );

  const table = useReactTable({
    data: tableConfig.data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: tableConfig.pageSize,
      },
    },
  });

  // Apply dashboard mode styling
  const isDashboardMode = properties?.dashboardMode === true;
  const rowSpacing = (typeof properties?.rowSpacing === 'string' ? properties.rowSpacing : isDashboardMode ? "tight" : "normal");
  const rowPadding = (typeof properties?.rowPadding === 'string' ? properties.rowPadding : isDashboardMode ? "compact" : "normal");
  const headerStyle = (typeof properties?.headerStyle === 'string' ? properties.headerStyle : isDashboardMode ? "typography" : "background");
  const showBorders = properties?.showBorders !== false;
  const headerBgColor = typeof properties?.headerBackgroundColor === 'string' ? properties.headerBackgroundColor : undefined;
  const headerTxtColor = typeof properties?.headerTextColor === 'string' ? properties.headerTextColor : undefined;
  const bodyTextColor = typeof properties?.bodyTextColor === 'string' ? properties.bodyTextColor : undefined;
  const alternateRowColor = typeof properties?.alternateRowColor === 'string' ? properties.alternateRowColor : undefined;
  const rowHoverColor = typeof properties?.rowHoverColor === 'string' ? properties.rowHoverColor : undefined;
  const tableBorderColor = typeof properties?.borderColor === 'string' ? properties.borderColor : undefined;
  const tableFontSize = getStringOrNumberProperty(properties?.fontSize);
  const paginationFontSize = getStringOrNumberProperty(properties?.paginationFontSize);
  const paginationPadding = getStringProperty(properties?.paginationPadding) ?? '1rem 0';
  const paginationButtonSize = isPaginationButtonSize(properties?.paginationButtonSize)
    ? properties.paginationButtonSize
    : "sm";
  const headerBackgroundStyle: React.CSSProperties = headerBgColor
    ? { backgroundColor: headerBgColor }
    : visualStyle.backgroundColor !== undefined
      ? { backgroundColor: visualStyle.backgroundColor }
      : visualStyle.background !== undefined
        ? { background: visualStyle.background }
        : { backgroundColor: "hsl(var(--card))" };

  // Row spacing classes (respects showBorders toggle)
  const getRowSpacingClass = (spacing: string) => {
    if (!showBorders) return spacing === "loose" ? "py-1" : "";
    switch (spacing) {
      case "none": return "";
      case "tight": return "border-b";
      case "loose": return "border-b-2 py-1";
      default: return "border-b";
    }
  };

  // Row padding classes
  const getRowPaddingClass = (padding: string) => {
    switch (padding) {
      case "compact": return "px-2 py-1";
      case "spacious": return "px-6 py-3";
      default: return "px-4 py-2";
    }
  };
  const rowEditorFields = React.useMemo(
    () =>
      createTableRowEditorFields(
        baseColumns,
        resolvedTableData,
        rowEditorState?.rowData ?? {},
      ),
    [baseColumns, resolvedTableData, rowEditorState],
  );

  const commitResolvedRows = React.useCallback(
    (nextRows: TableRowData[]) => {
      if (!canEditLocalRows && !canMutateSourceRows) return;

      setLocalRowsOverride(nextRows);
      if (canEditLocalRows) {
        commitLocalTableRows(component, nextRows);
      }
    },
    [canEditLocalRows, canMutateSourceRows, component],
  );

  const openCreateRowEditor = React.useCallback(() => {
    if (!canAddLocalRows) return;

    setRowEditorState({
      mode: "create",
      rowData: { id: generateLocalTableRowId() },
    });
  }, [canAddLocalRows]);

  const openEditRowEditor = React.useCallback(
    (rowIndex: number, rowData: TableRowData) => {
      if (import.meta.env.DEV) {
        console.debug("[BasicTable] open row editor requested", {
          componentId: component.id,
          rowIndex,
          canOpenRowEditor,
          columnCount: baseColumns.length,
          rowKeys: Object.keys(rowData),
        });
      }

      if (!canOpenRowEditor) return;

      setRowEditorState({
        mode: "edit",
        rowIndex,
        rowData,
      });
    },
    [baseColumns.length, canOpenRowEditor, component.id],
  );

  const tableRows = table.getRowModel().rows;
  const handleTableRowClickCapture = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const interactiveTarget = isInteractiveTableTarget(event.target);
      if (interactiveTarget) {
        if (import.meta.env.DEV) {
          console.debug("[BasicTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            reason: "interactive-target",
          });
        }
        return;
      }

      const rowPosition = getDelegatedTableRowIndex(event.target);
      if (rowPosition === null) {
        if (import.meta.env.DEV) {
          console.debug("[BasicTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            reason: "missing-row-index",
          });
        }
        return;
      }

      const row = tableRows[rowPosition];
      if (!row) {
        if (import.meta.env.DEV) {
          console.debug("[BasicTable] row click capture ignored", {
            componentId: component.id,
            targetTag: target?.tagName,
            rowPosition,
            rowCount: tableRows.length,
            reason: "missing-row",
          });
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.debug("[BasicTable] row click capture", {
          componentId: component.id,
          targetTag: target?.tagName,
          rowPosition,
          rowIndex: row.index,
          canOpenRowEditor,
          columnCount: baseColumns.length,
        });
      }

      event.stopPropagation();
      openEditRowEditor(row.index, row.original);
    },
    [baseColumns.length, canOpenRowEditor, component.id, openEditRowEditor, tableRows],
  );

  const getEditablePayload = React.useCallback(
    (row: Record<string, unknown>) => {
      const editableKeys = new Set(rowEditorFields.map((field) => field.key));
      const payload: Record<string, unknown> = {};

      Object.entries(row).forEach(([key, value]) => {
        if (editableKeys.has(key)) {
          payload[key] = value;
        }
      });

      return payload;
    },
    [rowEditorFields],
  );

  const saveRowEditor = React.useCallback(
    async (nextRow: Record<string, unknown>) => {
      if (!rowEditorState) return;

      if (rowEditorState.mode === "create") {
        const nextRowId =
          typeof nextRow.id === "string" && nextRow.id.length > 0
            ? nextRow.id
            : generateLocalTableRowId();
        const rowWithId: TableRowData = { ...nextRow, id: nextRowId };
        const nextRows = [...resolvedTableData, rowWithId];
        if (canEditLocalRows) {
          commitResolvedRows(nextRows);
          setRowEditorState(null);
          return;
        }

        const didCreate = await createRecord(getEditablePayload(nextRow), {
          metadata: { source: "table", tags: ["table"] },
          onOptimisticUpdate: () => commitResolvedRows(nextRows),
          onRollback: () => setLocalRowsOverride(resolvedTableData),
        });
        if (didCreate) {
          setRowEditorState(null);
        }
      } else {
        const nextRows = resolvedTableData.map((row, index) =>
          index === rowEditorState.rowIndex
            ? { ...row, ...nextRow }
            : row,
        );
        if (canEditLocalRows) {
          commitResolvedRows(nextRows);
          setRowEditorState(null);
          return;
        }

        const recordId = getTableRowRecordId(rowEditorState.rowData);
        if (recordId === undefined) return;

        const didUpdate = await updateRecord(recordId, getEditablePayload(nextRow), {
          onOptimisticUpdate: () => commitResolvedRows(nextRows),
          onRollback: () => setLocalRowsOverride(resolvedTableData),
        });
        if (didUpdate) {
          setRowEditorState(null);
        }
      }
    },
    [
      canEditLocalRows,
      commitResolvedRows,
      createRecord,
      getEditablePayload,
      resolvedTableData,
      rowEditorState,
      updateRecord,
    ],
  );

  const deleteEditedRow = React.useCallback(async () => {
    if (!rowEditorState || rowEditorState.mode !== "edit") return;

    const nextRows = resolvedTableData.filter((_, index) => index !== rowEditorState.rowIndex);
    if (canEditLocalRows) {
      commitResolvedRows(nextRows);
      setRowEditorState(null);
      return;
    }

    const recordId = getTableRowRecordId(rowEditorState.rowData);
    if (recordId === undefined) return;

    const didDelete = await deleteRecord(recordId, {
      onOptimisticUpdate: () => commitResolvedRows(nextRows),
      onRollback: () => setLocalRowsOverride(resolvedTableData),
    });
    if (didDelete) {
      setRowEditorState(null);
    }
  }, [
    canEditLocalRows,
    commitResolvedRows,
    deleteRecord,
    resolvedTableData,
    rowEditorState,
  ]);

  if (loading) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
            {typeof properties?.description === 'string' && properties.description && (
              <p className="text-sm text-muted-foreground">{properties.description}</p>
            )}
          </div>
        )}
        <div className="flex-1 min-h-[200px]">
          <TableLoadingSkeleton />
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
            {typeof properties?.description === 'string' && properties.description && (
              <p className="text-sm text-muted-foreground">{properties.description}</p>
            )}
          </div>
        )}
        <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] space-y-2">
          <AlertCircle className="w-8 h-8 text-destructive" />
          <p className="text-sm text-destructive">{errorMessage}</p>
          {resolvedData.reconnect && (
            <p className="text-xs text-muted-foreground">Reconnect source</p>
          )}
          <Button variant="outline" size="sm" onClick={() => void resolvedData.refetch()}>
            {resolvedData.reconnect ? "Reconnect" : "Retry"}
          </Button>
        </div>
      </div>
    );
  }

  if (tableConfig.data.length === 0 && !loading && !error && !canAddLocalRows) {
    return (
      <div className={cardClasses} style={cardStyle}>
        {typeof properties?.title === 'string' && properties.title && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
          </div>
        )}
        <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] text-muted-foreground">
          <p className="text-sm">No data to display</p>
          <p className="text-xs mt-1">Connect a data source or add data to this table</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses} style={cardStyle}>
      {typeof properties?.title === 'string' && properties.title && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{properties.title}</h3>
          {typeof properties?.description === 'string' && properties.description && (
            <p className="text-sm text-muted-foreground">
              {properties.description}
            </p>
          )}
        </div>
      )}

      {/* Global Filter */}
      {tableConfig.enableGlobalFilter && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <Input
            placeholder="Search all columns..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm"
          />
          {canAddLocalRows ? (
            <Button type="button" size="sm" onClick={openCreateRowEditor}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          ) : null}
        </div>
      )}
      {!tableConfig.enableGlobalFilter && canAddLocalRows ? (
        <div className="mb-4 flex justify-end">
          <Button type="button" size="sm" onClick={openCreateRowEditor}>
            <Plus className="mr-2 h-4 w-4" />
            New
          </Button>
        </div>
      ) : null}

      {/* Table */}
      <div
        className={usesContentDrivenHeight ? "overflow-visible" : "flex-1 min-h-0 overflow-hidden"}
        data-table-renderer-kind="basic"
      >
        <div
          className={
            tableConfig.maxHeight
              ? "overflow-auto"
              : usesContentDrivenHeight
                ? "overflow-x-auto overflow-y-visible"
                : "h-full overflow-auto"
          }
          style={tableConfig.maxHeight ? { maxHeight: `${tableConfig.maxHeight}px` } : undefined}
        >
          <table
            className="w-full text-sm"
            style={{
              ...(tableFontSize !== undefined ? { fontSize: tableFontSize } : {}),
              ...(rowHoverColor ? ({ "--tm-row-hover": rowHoverColor } satisfies TableStyle) : {}),
            }}
          >
            <thead
              style={headerBackgroundStyle}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`h-12 text-left align-middle ${
                        headerStyle === "typography"
                          ? "font-semibold text-foreground"
                          : headerTxtColor ? "font-medium" : "font-medium text-muted-foreground"
                      } ${showBorders ? "border-b" : ""} ${getRowPaddingClass(rowPadding)}`}
                      style={{ width: header.getSize(), ...(headerTxtColor ? { color: headerTxtColor } : {}) }}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className={
                            header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : ""
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                        {header.column.getCanSort() && (
                          <div className="flex flex-col">
                            {header.column.getIsSorted() === "asc" && (
                              <ChevronUp className="h-3 w-3" />
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <ChevronDown className="h-3 w-3" />
                            )}
                            {!header.column.getIsSorted() && (
                              <div className="h-3 w-3" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Column Filter */}
                      {header.column.getCanFilter() &&
                        tableConfig.enableFiltering && (
                          <div className="mt-1">
                            <Input
                              placeholder={`Filter ${header.column.columnDef.header}...`}
                              value={getStringProperty(header.column.getFilterValue()) ?? ""}
                              onChange={(e) =>
                                header.column.setFilterValue(e.target.value)
                              }
                              className="h-8 text-xs"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody onClickCapture={handleTableRowClickCapture}>
              {tableRows.map((row, index) => (
                <tr
                  key={row.id}
                  data-table-row-index={index}
                  className={`
                    ${getRowSpacingClass(rowSpacing)}
                    ${tableConfig.striped && index % 2 === 1 && !alternateRowColor ? "bg-muted/25" : ""}
                    ${tableConfig.hoverable ? (rowHoverColor ? "hover:bg-[var(--tm-row-hover)]" : "hover:bg-muted/50") : ""}
                    ${canInteractWithRows ? "cursor-pointer" : ""}
                  `}
                  style={{
                    ...(tableConfig.striped && index % 2 === 1 && alternateRowColor ? { backgroundColor: alternateRowColor } : {}),
                    ...(tableBorderColor ? { borderBottomColor: tableBorderColor } : {}),
                  }}
                  tabIndex={canInteractWithRows ? 0 : undefined}
                  onClick={(event) => {
                    if (isInteractiveTableTarget(event.target)) return;
                    event.stopPropagation();
                    openEditRowEditor(row.index, row.original);
                  }}
                  onKeyDown={(event) => {
                    if (!canInteractWithRows) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    if (isInteractiveTableTarget(event.target)) return;
                    event.preventDefault();
                    event.stopPropagation();
                    openEditRowEditor(row.index, row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`align-middle ${getRowPaddingClass(rowPadding)}`}
                      style={{
                        ...(bodyTextColor ? { color: bodyTextColor } : {}),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {tableConfig.enablePagination && (
        <div
          className={`flex items-center ${properties?.paginationWrap ? 'flex-wrap gap-2' : 'justify-between space-x-2'} py-4`}
          style={{
            fontSize: paginationFontSize,
            padding: paginationPadding,
          }}
        >
          <div className="text-xs text-muted-foreground">
            Showing{" "}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}{" "}
            to{" "}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length,
            )}{" "}
            of {table.getFilteredRowModel().rows.length} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size={paginationButtonSize}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </div>
            <Button
              variant="outline"
              size={paginationButtonSize}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      <TableRowDataEditor
        open={rowEditorState !== null}
        mode={rowEditorState?.mode ?? "edit"}
        fields={rowEditorFields}
        rowData={rowEditorState?.rowData ?? {}}
        mobileViewport={mobileViewport}
        readOnly={rowEditorState?.mode === "edit" && !canEditRows}
        onOpenChange={(open) => {
          if (!open) setRowEditorState(null);
        }}
        onSave={saveRowEditor}
        onDelete={
          rowEditorState?.mode === "edit" && (canEditLocalRows || canDeleteSourceRows)
            ? deleteEditedRow
            : undefined
        }
      />
    </div>
  );
};
